<template>
  <BaseLayout :full-width="true">
    <!-- 骨架屏加载状态 -->
    <template v-if="initializing">
      <!-- 单系统授权骨架屏 -->
      <div v-if="currentSystem" class="w-full px-3 sm:px-6">
        <!-- 骨架屏头部 -->
        <div class="flex flex-col items-center pb-6 sm:pb-12 animate-pulse">
          <div class="h-8 bg-gray-200 rounded-md w-72 mb-3"></div>
          <div class="h-4 bg-gray-200 rounded-md w-48"></div>
        </div>

        <!-- 骨架屏系统信息卡片 -->
        <div
          class="mt-6 sm:mt-8 overflow-hidden rounded-lg bg-white shadow-sm max-w-2xl mx-auto animate-pulse"
        >
          <div class="px-4 py-4 sm:px-6 sm:py-5">
            <div class="flex items-center space-x-4 sm:space-x-5">
              <div class="flex-shrink-0">
                <div
                  class="h-12 w-12 sm:h-16 sm:w-16 rounded-lg bg-gray-200"
                ></div>
              </div>
              <div class="min-w-0 flex-1 space-y-2">
                <div class="h-6 bg-gray-200 rounded w-3/4"></div>
                <div class="h-4 bg-gray-200 rounded w-full"></div>
              </div>
            </div>
          </div>
          <div class="border-t border-gray-15 px-4 py-4 sm:px-6 sm:py-5">
            <div class="h-5 bg-gray-200 rounded w-48 mb-4"></div>
            <div class="space-y-3 sm:space-y-4">
              <div
                v-for="n in 2"
                :key="n"
                class="rounded-lg bg-gray-50 p-3 sm:p-4"
              >
                <div class="flex items-center">
                  <div class="h-5 w-5 rounded bg-gray-200"></div>
                  <div class="ml-2 sm:ml-3 h-4 bg-gray-200 rounded w-1/3"></div>
                </div>
                <div
                  class="mt-1.5 sm:mt-2 h-3 bg-gray-200 rounded w-3/4 pl-6 sm:pl-8"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 骨架屏操作按钮 -->
        <div
          class="mt-6 sm:mt-8 flex justify-end gap-x-3 sm:gap-x-4 max-w-2xl mx-auto animate-pulse"
        >
          <div class="h-10 w-24 bg-gray-200 rounded-lg"></div>
          <div class="h-10 w-24 bg-gray-300 rounded-lg"></div>
        </div>
      </div>

      <!-- 系统选择骨架屏 -->
      <div v-else class="mx-auto w-full px-3 sm:px-6 lg:px-8">
        <!-- 骨架屏页面标题 -->
        <div class="flex flex-col items-center pb-6 sm:pb-12">
          <div class="animate-pulse">
            <div class="h-8 bg-gray-200 rounded-md w-64 mb-3"></div>
            <div class="h-4 bg-gray-200 rounded-md w-48"></div>
          </div>
        </div>

        <!-- 骨架屏系统列表 -->
        <div
          class="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 max-w-4xl mx-auto"
        >
          <div
            v-for="n in 4"
            :key="n"
            class="animate-pulse col-span-1 rounded-lg bg-white shadow-sm relative overflow-hidden"
          >
            <!-- 骨架屏系统信息 -->
            <div
              class="relative flex w-full items-center justify-between space-x-4 p-4 sm:space-x-6 sm:p-6"
            >
              <div class="flex-1 min-w-0">
                <div class="flex items-center space-x-2 sm:space-x-3 mb-2">
                  <div class="h-5 bg-gray-200 rounded w-32"></div>
                  <div
                    class="h-4 bg-gray-200 rounded-full w-16 px-2 py-0.5"
                  ></div>
                </div>
                <div class="h-3 bg-gray-200 rounded w-48"></div>
              </div>
              <div class="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0">
                <div class="h-full w-full bg-gray-200 rounded-lg"></div>
              </div>
            </div>

            <!-- 骨架屏系统操作 -->
            <div
              class="relative mt-1 sm:mt-2 before:absolute before:inset-x-4 sm:before:inset-x-6 before:top-0 before:h-[1px] before:bg-gray-200"
            >
              <div class="flex w-full">
                <div
                  class="relative inline-flex w-full items-center justify-center gap-x-2 sm:gap-x-3 py-3 sm:py-4"
                >
                  <div class="h-4 w-4 bg-gray-200 rounded"></div>
                  <div class="h-4 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- 初始化错误状态 -->
    <template v-else-if="initializationError">
      <div class="flex flex-col items-center justify-center min-h-[400px] px-4">
        <div class="max-w-md mx-auto text-center">
          <div class="flex justify-center mb-4">
            <ExclamationTriangleIcon class="h-12 w-12 text-red-500" />
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            {{ t("common.loadingError") }}
          </h3>
          <p class="text-gray-500 text-sm mb-6">
            {{ initializationError }}
          </p>
          <div class="flex justify-center space-x-3">
            <button
              type="button"
              @click="initializeComponent"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-6 hover:bg-primary-5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-6"
            >
              {{ t("common.retry") }}
            </button>
            <button
              type="button"
              @click="$router.push('/login')"
              class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-6"
            >
              {{ t("common.backToLogin") }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- 系统列表视图 -->
    <template v-else-if="!currentSystem">
      <div class="mx-auto w-full px-3 sm:px-6 lg:px-8">
        <!-- 页面标题区域 -->
        <div class="flex flex-col items-center pb-6 sm:pb-12">
          <PageTitle
            :title="
              t('authorize.welcome', {
                systemShortName: brandConfig.systemShortName,
              })
            "
            :subtitle="
              t('authorize.welcomeSubtitle', {
                systemFullName: brandConfig.systemFullName,
              })
            "
          />
        </div>

        <!-- 系统列表 -->
        <div
          v-if="availableSystems.length > 0"
          class="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 max-w-4xl mx-auto"
        >
          <div
            v-for="system in availableSystems"
            :key="system.name"
            class="group col-span-1 rounded-lg bg-white shadow-sm transition-all duration-200 hover:shadow-md relative overflow-hidden"
            :class="[
              isSystemFullyAvailable(system) ? 'cursor-pointer' : 'opacity-75',
              system.comingSoon ? 'grayscale' : '',
            ]"
            @click="
              isSystemFullyAvailable(system) && handleSelectSystem(system)
            "
          >
            <!-- 装饰性背景 -->
            <div
              class="absolute inset-0 bg-gradient-to-br from-primary-1/50 to-transparent pointer-events-none"
            />

            <!-- 系统信息 -->
            <div
              class="relative flex w-full items-center justify-between space-x-4 p-4 sm:space-x-6 sm:p-6"
            >
              <div class="flex-1 min-w-0">
                <div class="flex items-center space-x-2 sm:space-x-3">
                  <h3
                    class="truncate text-base sm:text-lg font-medium text-gray-85 flex items-center"
                  >
                    {{ safeSystemTranslation(system.name, "title") }}
                    <span
                      :class="[
                        getSystemStatusClass(system),
                        'inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ml-2 sm:ml-3',
                      ]"
                    >
                      {{ getSystemStatusText(system) }}
                    </span>
                  </h3>
                </div>
                <p class="mt-1 text-xs sm:text-sm text-gray-45 line-clamp-2">
                  {{ safeSystemTranslation(system.name, "description") }}
                </p>
              </div>
              <div class="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0">
                <div
                  class="flex h-full w-full items-center justify-center rounded-lg bg-primary-1/50 group-hover:bg-primary-2 transition-colors duration-200"
                >
                  <component
                    :is="getSystemIcon(system.name)"
                    class="h-5 w-5 sm:h-6 sm:w-6 text-primary-6"
                    :class="{ 'text-gray-45': !isSystemFullyAvailable(system) }"
                  />
                </div>
              </div>
            </div>

            <!-- 系统操作 -->
            <div
              class="relative mt-1 sm:mt-2 before:absolute before:inset-x-4 sm:before:inset-x-6 before:top-0 before:h-[1px] before:bg-gradient-to-r before:from-transparent before:via-gray-200/70 before:to-transparent"
            >
              <div class="flex w-full">
                <div
                  class="relative inline-flex w-full items-center justify-center gap-x-2 sm:gap-x-3 py-3 sm:py-4 text-sm font-semibold text-gray-85 group-hover:text-primary-6 transition-colors duration-200"
                >
                  <component
                    :is="getSystemActionIcon(system)"
                    class="h-4 w-4 sm:h-5 sm:w-5 text-gray-45 group-hover:text-primary-6 transition-colors duration-200"
                  />
                  {{ getSystemActionText(system) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态处理 -->
        <div
          v-else
          class="flex flex-col items-center justify-center py-12 px-4"
        >
          <div class="text-center">
            <!-- 显示系统logo而不是ServerIcon -->
            <div
              class="mx-auto mb-6 h-16 w-16 flex items-center justify-center"
            >
              <img
                :src="systemLogo"
                alt="System Logo"
                class="h-full w-full object-contain"
              />
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">
              {{
                t("authorize.welcome", {
                  systemShortName: brandConfig.systemShortName,
                })
              }}
            </h3>
            <p class="text-sm text-gray-500">
              {{ t("authorize.noConfigurationAvailable") }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <!-- 单系统授权视图 -->
    <template v-else>
      <div class="w-full px-3 sm:px-6">
        <!-- 授权头部 -->
        <div class="text-center">
          <PageTitle
            :title="
              t('authorize.title', {
                system: currentSystem
                  ? safeSystemTranslation(currentSystem.name, 'title')
                  : '',
              })
            "
            :subtitle="t('authorize.subtitle')"
          />
        </div>

        <!-- 系统不可用提示 -->
        <div
          v-if="!isSystemFullyAvailable(currentSystem)"
          class="mt-6 sm:mt-8 max-w-2xl mx-auto"
        >
          <div class="rounded-lg bg-yellow-50 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <ExclamationTriangleIcon
                  class="h-5 w-5 text-yellow-400"
                  aria-hidden="true"
                />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-yellow-800">
                  {{ getSystemStatusText(currentSystem) }}
                </h3>
                <div class="mt-2 text-sm text-yellow-700">
                  <p>{{ getSystemStatusDesc(currentSystem) }}</p>
                </div>
                <div class="mt-4">
                  <div class="-mx-2 -my-1.5 flex">
                    <button
                      type="button"
                      class="rounded-md bg-yellow-50 px-2 py-1.5 text-sm font-medium text-yellow-800 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-yellow-600 focus:ring-offset-2 focus:ring-offset-yellow-50"
                      @click="handleCancel"
                    >
                      {{ t("authorize.backToHome") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 系统信息卡片 -->
        <div
          v-if="isSystemFullyAvailable(currentSystem)"
          class="mt-6 sm:mt-8 overflow-hidden rounded-lg bg-white shadow-sm max-w-2xl mx-auto"
        >
          <div class="px-4 py-4 sm:px-6 sm:py-5">
            <div class="flex items-center space-x-4 sm:space-x-5">
              <div class="flex-shrink-0">
                <div
                  class="flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-lg bg-primary-1"
                >
                  <component
                    :is="getSystemIcon(currentSystem.name)"
                    class="h-6 w-6 sm:h-8 sm:w-8 text-primary-6"
                  />
                </div>
              </div>
              <div class="min-w-0 flex-1">
                <h2 class="text-lg sm:text-xl font-semibold text-gray-85">
                  {{ safeSystemTranslation(currentSystem.name, "title") }}
                </h2>
                <p class="mt-1 text-xs sm:text-sm text-gray-45">
                  {{ safeSystemTranslation(currentSystem.name, "description") }}
                </p>
              </div>
            </div>
          </div>

          <div class="border-t border-gray-15 px-4 py-4 sm:px-6 sm:py-5">
            <h3 class="text-sm sm:text-base font-medium text-gray-85">
              {{ t("authorize.permissionsRequired") }}
            </h3>

            <!-- 权限列表 -->
            <div class="mt-3 sm:mt-4 space-y-3 sm:space-y-4">
              <div
                v-for="(permission, key) in permissions"
                :key="key"
                class="rounded-lg bg-gray-2 p-3 sm:p-4 transition-all hover:bg-gray-50"
              >
                <div class="flex items-center">
                  <component
                    :is="permission.icon"
                    class="h-4 w-4 sm:h-5 sm:w-5 text-primary-6"
                  />
                  <span
                    class="ml-2 sm:ml-3 text-xs sm:text-sm font-medium text-gray-85"
                  >
                    {{ t(`authorize.permissions.${key}.title`) }}
                  </span>
                </div>
                <p
                  class="mt-1.5 sm:mt-2 text-xs sm:text-sm text-gray-45 pl-6 sm:pl-8"
                >
                  {{ t(`authorize.permissions.${key}.desc`) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div
          v-if="isSystemFullyAvailable(currentSystem)"
          class="mt-6 sm:mt-8 flex justify-end gap-x-3 sm:gap-x-4 max-w-2xl mx-auto"
        >
          <button
            type="button"
            class="rounded-lg bg-white px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold text-gray-85 shadow-sm hover:bg-gray-2 transition-colors duration-200"
            @click="handleCancel"
            :disabled="loading || verifying"
          >
            {{ t("authorize.cancel") }}
          </button>
          <button
            type="button"
            class="rounded-lg bg-primary-6 px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-5 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            :disabled="loading || verifying"
            @click="handleAuthorize"
          >
            <div class="flex items-center space-x-1.5">
              <svg
                v-if="verifying || loading"
                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span v-if="verifying">{{ t("authorize.verifying") }}</span>
              <span v-else-if="loading">{{ t("authorize.processing") }}</span>
              <span v-else>{{ t("authorize.confirm") }}</span>
            </div>
          </button>
        </div>
      </div>
    </template>
  </BaseLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useBrandConfig } from "@/utils/brand";
import { notificationService } from "@/services/notificationService";
import { systemConfigService } from "@/services/systemConfigService";
import {
  ArrowRightIcon,
  LockClosedIcon,
  UserIcon,
  AcademicCapIcon,
  UserGroupIcon,
  CubeIcon,
  ServerIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  WrenchIcon,
  XCircleIcon,
} from "@heroicons/vue/24/outline";
import BaseLayout from "@/components/layout/BaseLayout.vue";
import PageTitle from "@/components/common/PageTitle.vue";
import {
  getSystemConfig,
  validateSystemUrl,
  getSystems,
} from "@/router/systems";
import { useSystemStatusStore } from "@/stores/systemStatus";
import type { RuntimeSystemConfig } from "@/types/system";
import { jwtService } from "@/services/jwt";
import * as Sentry from "@sentry/vue";
import { reportMessage, reportError } from "@/utils/sentry";
import { verifyToken as apiVerifyToken } from "@/api/auth";
import { checkPasswordChanged } from "@/api/auth";
import { bindWechatOpenid } from "@/api/wechat";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const brandConfig = useBrandConfig();
const systemStatusStore = useSystemStatusStore();

const loading = ref(false);
const verifying = ref(false);
const initializing = ref(true);
const initializationError = ref<string | null>(null);

// 权限配置
const permissions = {
  profile: {
    icon: UserIcon,
    required: true,
  },
  classInfo: {
    icon: AcademicCapIcon,
    required: true,
  },
};

// 获取当前系统配置
const currentSystem = computed<RuntimeSystemConfig | null>(() => {
  if (route.meta.system) {
    return route.meta.system as RuntimeSystemConfig;
  }
  const systemName = route.query.system as string;
  const systems = getSystems();
  return systemName ? systems[systemName] : null;
});

// 可用系统列表
const availableSystems = computed<RuntimeSystemConfig[]>(() =>
  Object.values(getSystems()),
);

// 获取系统logo路径
const systemLogo = computed(() => {
  return systemConfigService.systemLogo;
});

// Heroicon 映射
const iconMap = {
  UserGroupIcon,
  AcademicCapIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ServerIcon,
  CubeIcon, // 默认图标
};

// 获取系统图标 - 使用配置中的 heroicon
function getSystemIcon(systemName: string) {
  const system = getSystems()[systemName];

  // 如果系统不存在，返回默认图标
  if (!system) {
    return iconMap.CubeIcon;
  }

  // 尝试获取配置中的 heroicon
  const heroiconName = system.heroicon;
  if (heroiconName && iconMap[heroiconName as keyof typeof iconMap]) {
    return iconMap[heroiconName as keyof typeof iconMap];
  }

  // 兜底：使用默认图标
  return iconMap.CubeIcon;
}

// 安全的系统翻译函数 - 有兜底处理
function safeSystemTranslation(
  systemName: string,
  field: "title" | "description",
): string {
  try {
    const key = `systems.${systemName}.${field}`;
    const translation = t(key);

    // 如果翻译就是 key 本身，说明翻译不存在
    if (translation === key) {
      // 使用系统名称作为兜底
      if (field === "title") {
        return systemName.charAt(0).toUpperCase() + systemName.slice(1);
      } else {
        return `${systemName} system`;
      }
    }

    return translation;
  } catch (error) {
    // 翻译失败时的兜底
    console.warn(
      `Translation failed for systems.${systemName}.${field}:`,
      error,
    );
    if (field === "title") {
      return systemName.charAt(0).toUpperCase() + systemName.slice(1);
    } else {
      return `${systemName} system`;
    }
  }
}

// 检查系统是否完全可用（环境变量允许 + 系统状态正常）
function isSystemFullyAvailable(system: any): boolean {
  // 系统配置兜底检查
  if (!system || typeof system !== "object") {
    return false;
  }

  // 检查必要字段是否存在
  if (!system.name || typeof system.enabled !== "boolean") {
    return false;
  }

  // 首先检查环境变量是否允许系统启用
  if (!system.enabled) {
    return false;
  }

  // 然后检查系统状态，如果状态检查失败，仍允许系统可用
  try {
    return (
      systemStatusStore.isSystemAvailable(system.name) &&
      !systemStatusStore.isSystemInMaintenance(system.name)
    );
  } catch (error) {
    console.warn(`Failed to check system status for ${system.name}:`, error);
    // 状态检查失败时，只要系统启用就认为可用
    return true;
  }
}

// 检查系统是否处于维护状态
function isSystemInMaintenance(system: any): boolean {
  if (!system || !system.name) {
    return false;
  }

  try {
    return systemStatusStore.isSystemInMaintenance(system.name);
  } catch (error) {
    console.warn(
      `Failed to check maintenance status for ${system.name}:`,
      error,
    );
    return false;
  }
}

// 检查系统是否不可用（服务出现故障）
function isSystemDown(system: any): boolean {
  if (!system || !system.name) {
    return false;
  }

  try {
    return !systemStatusStore.isSystemAvailable(system.name);
  } catch (error) {
    console.warn(
      `Failed to check system availability for ${system.name}:`,
      error,
    );
    return false;
  }
}

// 获取系统状态样式
const getSystemStatusClass = (system: any) => {
  // 系统配置兜底检查
  if (!system || typeof system !== "object") {
    return "bg-gray-50 text-gray-600 ring-1 ring-gray-500/10";
  }

  if (system.comingSoon) {
    return "bg-yellow-50 text-yellow-800 ring-1 ring-yellow-600/20";
  }

  if (!system.enabled) {
    return "bg-gray-50 text-gray-600 ring-1 ring-gray-500/10";
  }

  if (isSystemDown(system)) {
    return "bg-red-50 text-red-700 ring-1 ring-red-600/20";
  }

  if (isSystemInMaintenance(system)) {
    return "bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20";
  }

  return "bg-green-50 text-green-700 ring-1 ring-green-600/20";
};

// 获取系统状态文本
const getSystemStatusText = (system: any) => {
  // 系统配置兜底检查
  if (!system || typeof system !== "object") {
    return t("authorize.systemStatus.inactive");
  }

  if (system.comingSoon) {
    return t("authorize.systemStatus.comingSoon");
  }

  if (!system.enabled) {
    return t("authorize.systemStatus.inactive");
  }

  if (isSystemDown(system)) {
    return t("status.down");
  }

  if (isSystemInMaintenance(system)) {
    return t("status.maintenance");
  }

  return t("authorize.systemStatus.active");
};

// 获取系统状态描述
const getSystemStatusDesc = (system: any) => {
  // 系统配置兜底检查
  if (!system || typeof system !== "object") {
    return t("authorize.systemStatus.inactiveDesc");
  }

  if (system.comingSoon) {
    return t("authorize.systemStatus.comingSoonDesc");
  }

  if (!system.enabled) {
    return t("authorize.systemStatus.inactiveDesc");
  }

  if (isSystemDown(system)) {
    return t("authorize.systemStatus.downDesc");
  }

  if (isSystemInMaintenance(system)) {
    return t("authorize.systemStatus.maintenanceDesc");
  }

  return "";
};

// 获取系统操作图标
const getSystemActionIcon = (system: any) => {
  // 系统配置兜底检查
  if (!system || typeof system !== "object") {
    return LockClosedIcon;
  }

  if (
    !system.enabled ||
    system.comingSoon ||
    isSystemDown(system) ||
    isSystemInMaintenance(system)
  ) {
    return LockClosedIcon;
  }
  return ArrowRightIcon;
};

// 获取系统操作文本
const getSystemActionText = (system: any) => {
  // 系统配置兜底检查
  if (!system || typeof system !== "object") {
    return t("authorize.systemNotEnabled");
  }

  if (!system.enabled) {
    return t("authorize.systemNotEnabled");
  }

  if (system.comingSoon) {
    return t("authorize.systemNotEnabled");
  }

  if (isSystemDown(system)) {
    return t("authorize.systemDown");
  }

  if (isSystemInMaintenance(system)) {
    return t("authorize.systemMaintenance");
  }

  return t("authorize.enter");
};

// 处理系统选择
async function handleSelectSystem(system: any) {
  // 系统配置兜底检查
  if (!system || typeof system !== "object" || !system.name) {
    console.warn("Invalid system configuration:", system);
    notificationService.error("common.systemError");
    return;
  }

  if (!isSystemFullyAvailable(system)) return;

  try {
    verifying.value = true;

    // 验证 token
    const isValid = await verifyToken();
    if (!isValid) {
      Sentry.addBreadcrumb({
        category: "auth",
        message: "Token验证失败，重定向到登录页...",
        level: "info",
      });
      return;
    }

    // 检查系统路径是否存在
    if (!system.path) {
      console.error(`System ${system.name} has no path configured`);
      notificationService.error("common.systemError");
      return;
    }

    // token 验证通过，进行路由跳转
    try {
      await router.push({
        path: system.path,
        query: route.query,
      });
    } catch (routerError) {
      reportError(
        routerError instanceof Error
          ? routerError
          : new Error(String(routerError)),
        {
          component: "AuthorizeView",
          action: "handleSelectSystem_navigation",
          system: system.name,
          targetPath: system.path,
        },
      );
      notificationService.error("common.navigationError");
      // 如果导航失败，也跳转到登录页
      await router.push({
        path: "/login",
        query: {
          redirect: route.fullPath,
          ...route.query,
        },
        replace: true,
      });
    }
  } catch (error) {
    reportError(error instanceof Error ? error : new Error(String(error)), {
      component: "AuthorizeView",
      action: "handleSelectSystem",
      system: system.name,
    });
    notificationService.error("common.systemError");
    // 确保在发生错误时也跳转到登录页
    await router.push({
      path: "/login",
      query: {
        redirect: route.fullPath,
        ...route.query,
      },
      replace: true,
    });
  } finally {
    verifying.value = false;
  }
}

// 验证Token
async function verifyToken() {
  verifying.value = true;

  try {
    const isValid = await jwtService.ensureValidTokens();
    if (!isValid) {
      notificationService.error("authorize.tokenExpired");
      // 等待路由跳转完成
      await router.push({
        path: "/login",
        query: {
          redirect: route.fullPath,
          ...route.query,
        },
        // 强制替换当前路由
        replace: true,
      });
      return false;
    }
    return true;
  } catch (error) {
    reportError(error instanceof Error ? error : new Error(String(error)), {
      component: "AuthorizeView",
      action: "verifyToken",
    });
    // 发生错误时也确保跳转到登录页
    notificationService.error("common.systemError");
    await router.push({
      path: "/login",
      query: {
        redirect: route.fullPath,
        ...route.query,
      },
      replace: true,
    });
    return false;
  } finally {
    verifying.value = false;
  }
}

// 处理取消授权
function handleCancel() {
  // 如果有微信 openid，保留它以便用户重新登录后进行绑定
  const wechatOpenid = route.query.wechat_openid as string;
  const query: any = {};

  if (wechatOpenid) {
    query.wechat_openid = wechatOpenid;
  }

  router.push({
    path: "/login",
    query,
  });
}

// 获取原始请求URL
function getOriginalRequestUrl(): string | null {
  // 系统配置兜底检查
  if (!currentSystem.value || !currentSystem.value.callback) {
    console.error("No current system or callback URL configured");
    return null;
  }

  // 1. 首先尝试从redirect参数获取，但需要严格验证
  const redirectParam = route.query.redirect as string;
  if (redirectParam && currentSystem.value?.callback) {
    try {
      const decodedUrl = decodeURIComponent(redirectParam);
      Sentry.addBreadcrumb({
        category: "auth",
        message: `处理重定向URL: ${decodedUrl}`,
        level: "info",
      });

      // 获取当前域名
      const currentOrigin = window.location.origin;

      // 处理相对路径的情况
      let redirectUrl: URL;
      if (decodedUrl.startsWith("/")) {
        // 如果是相对路径，使用当前域构建完整URL
        const fullUrl = currentOrigin + decodedUrl;
        Sentry.addBreadcrumb({
          category: "auth",
          message: `将相对路径转换为完整URL: ${fullUrl}`,
          level: "info",
        });
        redirectUrl = new URL(fullUrl);
      } else if (decodedUrl.includes("://")) {
        // 已经是完整URL的情况
        Sentry.addBreadcrumb({
          category: "auth",
          message: `使用完整URL: ${decodedUrl}`,
          level: "info",
        });
        redirectUrl = new URL(decodedUrl);
      } else {
        // 既不是相对路径也不是完整URL的情况
        reportMessage("无效的URL格式，回退到系统回调URL", "warning", {
          decodedUrl,
          systemName: currentSystem.value.name,
        });
        throw new Error("Invalid URL format: " + decodedUrl);
      }

      const systemCallback = new URL(currentSystem.value.callback);
      Sentry.addBreadcrumb({
        category: "auth",
        message: `系统回调URL: ${systemCallback.toString()}`,
        level: "info",
      });

      // 验证：重定向URL必须与子系统callback URL在同一域名下
      if (redirectUrl.origin === systemCallback.origin) {
        Sentry.addBreadcrumb({
          category: "auth",
          message: "重定向URL验证通过",
          level: "info",
        });
        return decodedUrl.startsWith("/")
          ? currentOrigin + decodedUrl
          : decodedUrl;
      } else {
        reportMessage("重定向URL验证失败：域名不匹配", "warning", {
          redirectUrlOrigin: redirectUrl.origin,
          systemCallbackOrigin: systemCallback.origin,
          redirectParam,
        });
      }
    } catch (e) {
      reportError(e instanceof Error ? e : new Error(String(e)), {
        component: "AuthorizeView",
        action: "validateRedirectUrl",
        redirectParam,
        systemCallback: currentSystem.value?.callback,
      });
    }
  }

  // 2. 如果没有有效的重定向URL，使用系统默认callback
  Sentry.addBreadcrumb({
    category: "auth",
    message: `使用系统默认回调URL: ${currentSystem.value?.callback}`,
    level: "info",
  });
  return currentSystem.value?.callback || null;
}

// 处理授权确认
async function handleAuthorize() {
  // 系统配置兜底检查
  if (
    !currentSystem.value ||
    typeof currentSystem.value !== "object" ||
    !currentSystem.value.name
  ) {
    console.error("Invalid current system configuration:", currentSystem.value);
    notificationService.error("common.systemError");
    return;
  }

  if (!isSystemFullyAvailable(currentSystem.value)) return;

  try {
    loading.value = true;

    // 验证 token
    const isValid = await verifyToken();
    if (!isValid) return;

    // 获取重定向URL
    const redirectUrl = getOriginalRequestUrl();

    if (!redirectUrl) {
      throw new Error(t("authorize.redirectUrlError"));
    }

    try {
      const targetUrl = new URL(redirectUrl);

      // 添加其他必要参数
      targetUrl.searchParams.set("client_id", currentSystem.value.name);
      targetUrl.searchParams.set("response_type", "token");
      targetUrl.searchParams.set("timestamp", Date.now().toString());

      // 跳转到目标系统
      window.location.href = targetUrl.toString();
    } catch (error) {
      reportError(error instanceof Error ? error : new Error(String(error)), {
        component: "AuthorizeView",
        action: "handleAuthorize_constructUrl",
        redirectUrl,
        currentSystem: currentSystem.value.name,
      });
      throw error;
    }
  } catch (error) {
    reportError(error instanceof Error ? error : new Error(String(error)), {
      component: "AuthorizeView",
      action: "handleAuthorize",
      currentSystem: currentSystem.value?.name,
    });
    notificationService.error("authorize.authorizationError");
    jwtService.clearTokens();
    router.push("/login");
  } finally {
    loading.value = false;
  }
}

// 防止重复检查的标志 - 改为组件内响应式变量
const isCheckingEmailBound = ref(false);

// 检查用户是否已绑定邮箱和是否已修改默认密码
async function checkEmailBound() {
  // 防止重复调用
  if (isCheckingEmailBound.value) {
    console.log("checkEmailBound already in progress, skipping duplicate call");
    return;
  }

  isCheckingEmailBound.value = true;
  try {
    const token = jwtService.getToken();
    if (!token) return;

    const payload = jwtService.parseToken(token);
    if (!payload) return;

    // 同时检查密码修改状态和邮箱绑定状态
    let passwordChanged = true; // 默认为已修改
    const isEmailBound = payload.isEmail !== false; // 检查isEmail字段，如果不存在或为true，则认为已绑定邮箱

    // 检查密码是否已修改
    try {
      const passwordStatus = await checkPasswordChanged();
      passwordChanged = passwordStatus.isPasswordChanged;
    } catch (error) {
      console.error("检查密码状态失败:", error);
      reportError(error instanceof Error ? error : new Error(String(error)), {
        component: "AuthorizeView",
        action: "checkPasswordChanged",
      });
    }

    // 同时处理邮箱未绑定和密码未修改的情况
    if (!isEmailBound && !passwordChanged) {
      // 两者都有问题，先提示邮箱绑定
      notificationService.confirm(
        "authorize.emailBindTitle",
        "authorize.emailAndPasswordRequired",
        handleGoToEmailBind,
        undefined,
        "authorize.bindNow",
        "authorize.bindLater",
      );

      // 记录日志
      Sentry.addBreadcrumb({
        category: "auth",
        message: "用户未绑定邮箱且未修改默认密码",
        level: "info",
      });
    } else if (!isEmailBound) {
      // 仅邮箱未绑定
      notificationService.confirm(
        "authorize.emailBindTitle",
        "authorize.emailBindRequired",
        handleGoToEmailBind,
        undefined,
        "authorize.bindNow",
        "authorize.bindLater",
      );

      // 记录日志
      Sentry.addBreadcrumb({
        category: "auth",
        message: "用户未绑定邮箱",
        level: "info",
      });
    } else if (!passwordChanged) {
      // 仅密码未修改
      notificationService.confirm(
        "authorize.passwordChangeTitle",
        "authorize.passwordChangeRequired",
        handleGoToPasswordChange,
        undefined,
        "authorize.changeNow",
        "authorize.changeLater",
      );

      // 记录日志
      Sentry.addBreadcrumb({
        category: "auth",
        message: "用户未修改默认密码",
        level: "info",
      });
    }
  } catch (error) {
    reportError(error instanceof Error ? error : new Error(String(error)), {
      component: "AuthorizeView",
      action: "checkEmailBound",
    });
  } finally {
    // 重置标志，允许下次调用
    isCheckingEmailBound.value = false;
  }
}

// 处理前往邮箱绑定页面
function handleGoToEmailBind() {
  router.push({
    path: "/email",
    query: {
      redirect: route.fullPath,
      ...route.query,
    },
  });
}

// 处理前往密码修改页面
function handleGoToPasswordChange() {
  router.push({
    path: "/password/modify",
    query: {
      redirect: route.fullPath,
      ...route.query,
    },
  });
}

// 静默绑定微信openid
async function performSilentWechatBinding() {
  const wechatOpenid = route.query.wechat_openid as string;

  if (!wechatOpenid) {
    return; // 没有openid参数，无需绑定
  }

  try {
    // 获取当前用户信息
    const token = jwtService.getToken();
    if (!token) {
      reportMessage("无法获取用户token，跳过微信绑定", "warning");
      return;
    }

    const payload = jwtService.parseToken(token);
    if (!payload || !payload.username) {
      reportMessage("无法解析用户信息，跳过微信绑定", "warning");
      return;
    }

    Sentry.addBreadcrumb({
      category: "wechat",
      message: "开始静默绑定微信账号",
      level: "info",
      data: {
        username: payload.username,
        openid: wechatOpenid.substring(0, 8) + "***",
      },
    });

    // 调用绑定接口，使用静默绑定（snsapi_base scope）
    const bindResult = await bindWechatOpenid(wechatOpenid, payload.username);

    if (bindResult.success) {
      // 绑定成功，更新token
      if (bindResult.access && bindResult.refresh) {
        await jwtService.setTokens(bindResult.access, bindResult.refresh);

        Sentry.addBreadcrumb({
          category: "wechat",
          message: "微信账号静默绑定成功",
          level: "info",
          data: {
            username: payload.username,
            openid: wechatOpenid.substring(0, 8) + "***",
          },
        });

        // 显示成功消息（静默，不打扰用户）
        notificationService.success("authorize.wechatBindSuccess");
      }
    } else {
      reportMessage("微信账号绑定失败", "warning", {
        username: payload.username,
        openid: wechatOpenid.substring(0, 8) + "***",
        message: bindResult.message,
      });
    }

    // 清理URL参数，移除openid避免重复绑定
    const newQuery = { ...route.query };
    delete newQuery.wechat_openid;

    await router.replace({
      path: route.path,
      query: newQuery,
    });
  } catch (error) {
    reportError(error instanceof Error ? error : new Error(String(error)), {
      component: "AuthorizeView",
      action: "performSilentWechatBinding",
      openid: wechatOpenid ? wechatOpenid.substring(0, 8) + "***" : "empty",
    });

    // 静默处理错误，不影响正常授权流程
    console.warn("微信静默绑定失败:", error);
  }
}

// 安全的异步初始化函数
async function safeAsyncOperation<T>(
  operation: () => Promise<T>,
  errorMessage: string,
): Promise<T | null> {
  try {
    return await operation();
  } catch (error) {
    console.error(errorMessage, error);
    reportError(error instanceof Error ? error : new Error(String(error)), {
      component: "AuthorizeView",
      action: "safeAsyncOperation",
      operation: errorMessage,
    });
    return null;
  }
}

// 并行初始化函数
async function initializeComponent() {
  initializing.value = true;
  initializationError.value = null;

  try {
    // 并行执行独立的异步操作
    const [statusResult, wechatResult] = await Promise.allSettled([
      safeAsyncOperation(
        () => systemStatusStore.refreshStatus(),
        "获取系统状态失败",
      ),
      safeAsyncOperation(
        () => performSilentWechatBinding(),
        "微信静默绑定失败",
      ),
    ]);

    // 检查关键操作是否成功
    if (statusResult.status === "rejected") {
      console.warn("系统状态获取失败，使用默认状态");
    }

    // 在下一个tick进行token验证和邮箱检查，确保DOM已更新
    await nextTick();

    // 检查邮箱绑定状态（统一处理，避免重复调用）
    if (currentSystem.value) {
      const isValid = await safeAsyncOperation(
        () => verifyToken(),
        "Token验证失败",
      );
      // 如果token有效，检查邮箱绑定状态
      if (isValid) {
        await safeAsyncOperation(() => checkEmailBound(), "邮箱绑定检查失败");
      }
    } else {
      // 在系统选择页面也需要检查邮箱绑定状态，但要确保已经有有效token
      const token = jwtService.getToken();
      if (token && !jwtService.isTokenExpiredLocally(token)) {
        await safeAsyncOperation(() => checkEmailBound(), "邮箱绑定检查失败");
      }
    }
  } catch (error) {
    console.error("组件初始化失败:", error);
    initializationError.value = "页面初始化失败，请刷新重试";
    reportError(error instanceof Error ? error : new Error(String(error)), {
      component: "AuthorizeView",
      action: "initializeComponent",
    });
  } finally {
    initializing.value = false;
  }
}

// 在组件挂载时进行初始化
onMounted(() => {
  // 使用非阻塞的方式初始化组件
  initializeComponent().catch((error) => {
    console.error("初始化组件时发生未捕获的错误:", error);
    initializing.value = false;
    initializationError.value = "页面加载失败，请刷新重试";
  });
});

// 监听路由变化，刷新系统状态
watch(
  () => route.fullPath,
  async () => {
    // 避免在初始化期间触发不必要的刷新
    if (!initializing.value) {
      try {
        await safeAsyncOperation(
          () => systemStatusStore.refreshStatus(),
          "路由变化时刷新系统状态失败",
        );
      } catch (error) {
        console.warn("路由变化时系统状态刷新失败:", error);
      }
    }
  },
);
</script>
